import { useState } from "react";
import { Key, User, Lock, AlertCircle } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../ui/dialog";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import { Button } from "../../ui/button";

interface AddCredentialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (credential: {
    label: string;
    username: string;
    password: string;
    autoRotate: boolean;
  }) => Promise<void> | void;
  users: any[];
  existingCredentials?: Array<{ credentialUsername?: string }>;
  isManualDevice?: boolean;
}

export default function AddCredentialDialog({
  open,
  onOpenChange,
  onAdd,
  users,
  existingCredentials = [],
  isManualDevice = false,
}: AddCredentialDialogProps) {
  const [formData, setFormData] = useState({
    label: "",
    username: "",
    password: "",
    autoRotate: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const { toast } = useToast();

  const validateUsername = (value: string) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return null;
    }

    if (!isManualDevice) {
      const duplicateCredential = existingCredentials.some(
        (credential) =>
          credential.credentialUsername?.trim().toLowerCase() === trimmedValue.toLowerCase()
      );

      if (duplicateCredential) {
        return "Username already exists for this device.";
      }
    }

    const matchedUser = users.find((user) => user.cn.toLowerCase() === trimmedValue.toLowerCase());
    return matchedUser ? null : "Username does not match any existing user.";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.label.trim()) {
      toast({ title: "Validation Error", description: "Credential label is required.", variant: "destructive" });
      return;
    }
    const currentUsernameError = validateUsername(formData.username);
    setUsernameError(currentUsernameError);

    if (!formData.username.trim() || currentUsernameError) {
      toast({ title: "Validation Error", description: "Username is invalid.", variant: "destructive" });
      return;
    }
    if (!formData.password.trim()) {
      toast({ title: "Validation Error", description: "Password is required.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      await Promise.resolve(onAdd(formData));
      toast({ title: "Credential Added", description: `Credential "${formData.label}" has been successfully created.` });

      setFormData({ label: "", username: "", password: "", autoRotate: false });
      setUsernameError(null);
      onOpenChange(false);
    } catch (error: any) {
      const description = typeof error === "string" ? error : error?.message || "Failed to create credential. Please try again.";
      toast({ title: "Error", description, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ label: "", username: "", password: "", autoRotate: false });
    setUsernameError(null);
    onOpenChange(false);
  };

  // Check if submit button should be enabled
  const isSubmitDisabled =
    !formData.label.trim() ||
    !formData.username.trim() ||
    !formData.password.trim() ||
    !!usernameError ||
    isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full dark:bg-blue-900/20">
              <Key className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">Add New Credential</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">
                Create a new credential for secure access management
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Credential Label */}
          <div className="space-y-2">
            <Label htmlFor="label" className="flex items-center gap-2 text-sm font-medium">
              <Key className="w-4 h-4" />
              Credential Label
            </Label>
            <Input
              id="label"
              type="text"
              value={formData.label}
              onChange={(e) => setFormData((prev) => ({ ...prev, label: e.target.value }))}
              placeholder="Enter credential label (e.g., admin-access)"
              className="w-full"
              disabled={isSubmitting}
              autoComplete="off"
            />
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username" className="flex items-center gap-2 text-sm font-medium">
              <User className="w-4 h-4" />
              Username
            </Label>
            <Input
              id="username"
              type="text"
              value={formData.username}
              onChange={(e) => {
                const value = e.target.value;
                setFormData((prev) => ({ ...prev, username: value }));
                setUsernameError(validateUsername(value));
              }}
              placeholder="Enter username"
              className="w-full"
              disabled={isSubmitting}
              autoComplete="username"
            />
            {usernameError && <p className="mt-1 text-xs text-red-600">{usernameError}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2 text-sm font-medium">
              <Lock className="w-4 h-4" />
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
              placeholder="Enter password"
              className="w-full"
              disabled={isSubmitting}
              autoComplete="new-password"
            />
          </div>

          {/* Auto-Rotate Password */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="auto-rotate" className="text-sm font-medium">
                Auto-Rotate Password
              </Label>
              <Switch
                id="auto-rotate"
                checked={formData.autoRotate}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, autoRotate: checked }))}
                disabled={isSubmitting}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              If enabled, the password will be automatically rotated daily for enhanced security.
            </p>
          </div>

          {/* Security Notice */}
          <div className="flex items-start gap-3 p-3 border rounded-md bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800">
            <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800 dark:text-amber-300">
              <p className="mb-1 font-medium">Security Notice</p>
              <p>Credentials are encrypted and stored securely. Ensure passwords meet your organization's security requirements.</p>
            </div>
          </div>

          {/* Footer Buttons */}
          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitDisabled} className="bg-blue-600 hover:bg-blue-700">
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                  Adding...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Add Credential
                </div>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
